/**
 * Modal 18 plis — toggle équivalence, comparaison côte à côte d'une bifurcation.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../../contexts/AppContext';
import { computeDealTrickSeatEquivVariantKeys } from '../../utils/differeStatsEquivVariants';
import { computeForcedPlaySeatKeys } from '../../utils/differeStatsForcedPlays';
import {
  buildReplaySeatLabels,
  buildTrainingGameDataFromStatsDetail,
  type DiffereStatsDealDetail,
  fetchDiffereStatsDealDetail,
  formatPerDealRankLabel,
  computeDealTrickSeatDiffKeys,
  diffHighlightOptionsForAlternative,
  humanCardsPlayedOnDeal,
  type TrickScoreAlternative,
} from '../../utils/differeStatsReplay';
import DiffereStatsDealTricksGrid from './DiffereStatsDealTricksGrid';
import DiffereStatsDealHandsPanel from './DiffereStatsDealHandsPanel';
import DiffereStatsEquivModeToggle, {
  type EquivMode,
} from './DiffereStatsEquivModeToggle';
import DiffereStatsPseudoPlusBubble from './DiffereStatsPseudoPlusBubble';
import DiffereStatsTrickHoverPreview, {
  type TrickHoverPreviewTarget,
} from './DiffereStatsTrickHoverPreview';

const SIDE_BY_SIDE_MIN_WIDTH = 1300;

type ComparePane = 'studied' | 'compare';

function findAltForResultId(
  detail: DiffereStatsDealDetail,
  resultId: number,
): TrickScoreAlternative | null {
  for (const slot of detail.betterScoreAlternativesByTrick ?? []) {
    for (const alt of slot) {
      if (alt.kind === 'equivalent') continue;
      if (Number(alt.bestResultId) === Number(resultId)) return alt;
    }
  }
  return null;
}

export default function DiffereStatsDealDetailModal({
  open,
  loading,
  error,
  studiedDetail,
  compareDetail,
  hypothesisCards,
  trickFocus,
  diffSeatKeys,
  sessionPseudo,
  topX,
  contractTypes,
  pageEquivMode,
  lowRankLimit,
  differeMeta,
  onClose,
  onStudiedDetail,
  onCompareDetail,
  onHypothesisCards,
  onTrickFocus,
  onDiffSeatKeys,
  onError,
  onLoading,
}: {
  open: boolean;
  loading: boolean;
  error: string | null;
  studiedDetail: DiffereStatsDealDetail | null;
  compareDetail: DiffereStatsDealDetail | null;
  hypothesisCards: number[] | null;
  trickFocus: number | null;
  diffSeatKeys: Set<string> | null;
  sessionPseudo: string;
  topX: number;
  contractTypes: string;
  pageEquivMode: EquivMode;
  lowRankLimit: number;
  /** Type / date de début du différé (affichage entête). */
  differeMeta?: { type?: string | null; debut?: string | null } | null;
  onClose: () => void;
  onStudiedDetail: (d: DiffereStatsDealDetail | null) => void;
  onCompareDetail: (d: DiffereStatsDealDetail | null) => void;
  onHypothesisCards: (cards: number[] | null) => void;
  onTrickFocus: (n: number | null) => void;
  onDiffSeatKeys: (keys: Set<string> | null) => void;
  onError: (e: string | null) => void;
  onLoading: (v: boolean) => void;
}) {
  const { startTraining } = useApp();
  const [modalEquivMode, setModalEquivMode] = useState<EquivMode>(pageEquivMode);
  const [smartBif, setSmartBif] = useState(true);
  const [narrowViewport, setNarrowViewport] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < SIDE_BY_SIDE_MIN_WIDTH,
  );
  const [activePane, setActivePane] = useState<ComparePane>('compare');
  const [compareAlt, setCompareAlt] = useState<TrickScoreAlternative | null>(null);
  const [hoverPreviewActive, setHoverPreviewActive] = useState(false);
  const [hoverPreview, setHoverPreview] = useState<{
    detail: DiffereStatsDealDetail;
    target: TrickHoverPreviewTarget;
    x: number;
    y: number;
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    setModalEquivMode(pageEquivMode);
    setSmartBif(true);
  }, [open, pageEquivMode]);

  useEffect(() => {
    if (!open) {
      setHoverPreviewActive(false);
      setHoverPreview(null);
    }
  }, [open]);

  useEffect(() => {
    if (!compareDetail) setCompareAlt(null);
  }, [compareDetail]);

  useEffect(() => {
    const onResize = () => setNarrowViewport(window.innerWidth < SIDE_BY_SIDE_MIN_WIDTH);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (compareDetail) setActivePane('compare');
  }, [compareDetail?.resultId]);

  const refetchParty = useCallback(async (
    target: { differeId: number; resultId: number; dealIndex: number },
    equivMode: EquivMode,
    smart: boolean,
  ) => fetchDiffereStatsDealDetail({
    sessionPseudo,
    topX,
    contractTypes,
    equivMode,
    lowRankLimit,
    smartBif: smart,
    ...target,
  }), [sessionPseudo, topX, contractTypes, lowRankLimit]);

  const refetchStudiedAndCompare = useCallback(async (
    nextEquiv: EquivMode,
    nextSmart: boolean,
  ) => {
    if (!studiedDetail) return;
    onLoading(true);
    onError(null);
    const openCompare = compareDetail;
    try {
      const studied = await refetchParty({
        differeId: studiedDetail.differeId,
        resultId: studiedDetail.resultId,
        dealIndex: studiedDetail.dealIndex,
      }, nextEquiv, nextSmart);
      onStudiedDetail(studied);

      if (openCompare) {
        const cmp = await refetchParty({
          differeId: openCompare.differeId,
          resultId: openCompare.resultId,
          dealIndex: openCompare.dealIndex,
        }, nextEquiv, nextSmart);
        onCompareDetail(cmp);
        const matchedAlt = findAltForResultId(studied, openCompare.resultId);
        setCompareAlt(matchedAlt);
        if (matchedAlt) {
          const focusTrickIndex = matchedAlt.effectTrickIndex ?? matchedAlt.trickIndex;
          const hypothesisDepth = Math.max(matchedAlt.trickIndex + 1, focusTrickIndex + 1);
          onHypothesisCards(humanCardsPlayedOnDeal(studied).slice(0, hypothesisDepth));
          onTrickFocus(focusTrickIndex + 1);
          onDiffSeatKeys(computeDealTrickSeatDiffKeys(
            studied,
            cmp,
            diffHighlightOptionsForAlternative(matchedAlt, studied.humanSeat ?? 0),
          ));
        } else {
          onDiffSeatKeys(computeDealTrickSeatDiffKeys(studied, cmp));
        }
      }
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      onLoading(false);
    }
  }, [
    studiedDetail, compareDetail, refetchParty,
    onStudiedDetail, onCompareDetail, onHypothesisCards, onTrickFocus,
    onDiffSeatKeys, onError, onLoading,
  ]);

  const setModalEquivAndRefetch = useCallback(async (next: EquivMode) => {
    if (!studiedDetail || next === modalEquivMode) return;
    setModalEquivMode(next);
    await refetchStudiedAndCompare(next, smartBif);
  }, [studiedDetail, modalEquivMode, smartBif, refetchStudiedAndCompare]);

  const setSmartBifAndRefetch = useCallback(async (next: boolean) => {
    if (!studiedDetail || next === smartBif) return;
    setSmartBif(next);
    await refetchStudiedAndCompare(modalEquivMode, next);
  }, [studiedDetail, smartBif, modalEquivMode, refetchStudiedAndCompare]);

  const openAlternative = useCallback(async (alt: TrickScoreAlternative, trickIndex: number) => {
    if (!studiedDetail) return;
    onLoading(true);
    onError(null);
    try {
      const focusTrickIndex = alt.effectTrickIndex ?? trickIndex;
      const hypothesisDepth = Math.max(trickIndex + 1, focusTrickIndex + 1);
      const cmp = await refetchParty({
        differeId: studiedDetail.differeId,
        resultId: alt.bestResultId,
        dealIndex: studiedDetail.dealIndex,
      }, modalEquivMode, smartBif);
      setCompareAlt(alt);
      onCompareDetail(cmp);
      onHypothesisCards(humanCardsPlayedOnDeal(studiedDetail).slice(0, hypothesisDepth));
      onTrickFocus(focusTrickIndex + 1);
      onDiffSeatKeys(computeDealTrickSeatDiffKeys(
        studiedDetail,
        cmp,
        diffHighlightOptionsForAlternative(alt, studiedDetail.humanSeat ?? 0),
      ));
      setActivePane('compare');
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      onLoading(false);
    }
  }, [
    studiedDetail, modalEquivMode, smartBif, refetchParty,
    onCompareDetail, onHypothesisCards, onTrickFocus, onDiffSeatKeys, onError, onLoading,
  ]);

  const clearCompare = useCallback(() => {
    setCompareAlt(null);
    onCompareDetail(null);
    onDiffSeatKeys(null);
    setActivePane('studied');
  }, [onCompareDetail, onDiffSeatKeys]);

  const promoteCompareToStudied = useCallback(() => {
    if (!compareDetail) return;
    onStudiedDetail(compareDetail);
    setCompareAlt(null);
    onCompareDetail(null);
    onDiffSeatKeys(null);
    onHypothesisCards(null);
    onTrickFocus(null);
    setActivePane('studied');
  }, [compareDetail, onStudiedDetail, onCompareDetail, onDiffSeatKeys, onHypothesisCards, onTrickFocus]);

  const openInTraining = useCallback((detail: DiffereStatsDealDetail) => {
    const gameData = buildTrainingGameDataFromStatsDetail(detail);
    const replaySeatLabels = buildReplaySeatLabels(detail);
    const label = `Différé #${detail.differeId} — ${detail.pseudoDisplay || detail.pseudo} — donne ${detail.dealIndex + 1}`;
    sessionStorage.setItem(
      'library_load_request',
      JSON.stringify({
        gameData,
        mode: 'replay',
        position: 0,
        replayAllBots: true,
        replaySeatLabels,
        compareReplayBatch: {
          title: label,
          deals: [{ dealIndex: detail.dealIndex, label, gameData, replaySeatLabels }],
          selectedIndex: 0,
        },
        historyContext: {
          kind: 'differe',
          differe: {
            differeId: detail.differeId,
            pseudo: detail.pseudo,
            pseudoDisplay: detail.pseudoDisplay || detail.pseudo,
            dealNum: detail.dealIndex + 1,
            statsDrill: true,
          },
        },
      }),
    );
    startTraining({
      sessionId: '',
      displayMode: 'cards_on_table',
      showTimeline: true,
      botConfig: { disabledBots: new Set() },
    });
  }, [startTraining]);

  const toggleTrickFocus = useCallback((trickNum1: number) => {
    onTrickFocus(trickFocus === trickNum1 ? null : trickNum1);
  }, [trickFocus, onTrickFocus]);

  const toggleHoverPreview = useCallback(() => {
    setHoverPreviewActive(v => {
      if (v) setHoverPreview(null);
      return !v;
    });
  }, []);

  const handleHoverPreviewTarget = useCallback((
    detail: DiffereStatsDealDetail,
    target: TrickHoverPreviewTarget | null,
    clientX: number,
    clientY: number,
  ) => {
    if (!target) {
      setHoverPreview(null);
      return;
    }
    setHoverPreview({ detail, target, x: clientX, y: clientY });
  }, []);

  const groupEquiv = modalEquivMode !== 'off';
  const forcedKeysStudied = useMemo(
    () => (studiedDetail
      ? computeForcedPlaySeatKeys(studiedDetail, groupEquiv, lowRankLimit)
      : undefined),
    [studiedDetail, groupEquiv, lowRankLimit],
  );
  const forcedKeysCompare = useMemo(
    () => (compareDetail
      ? computeForcedPlaySeatKeys(compareDetail, groupEquiv, lowRankLimit)
      : undefined),
    [compareDetail, groupEquiv, lowRankLimit],
  );
  const equivVariantKeys = useMemo(() => {
    if (!studiedDetail || !compareDetail || !groupEquiv) return null;
    // Parcourir tous les plis (pas seulement la fenêtre violet fromTrickIndex) :
    // en max+, les plis « sautés » (bots différents, issue équivalente) doivent rester teal.
    const keys = computeDealTrickSeatEquivVariantKeys(studiedDetail, compareDetail, {
      equivMode: modalEquivMode,
      lowRankLimit,
    });
    return keys.size > 0 ? keys : null;
  }, [studiedDetail, compareDetail, groupEquiv, modalEquivMode, lowRankLimit]);
  const bifurcationOriginKey = useMemo(() => {
    if (!compareAlt || !compareDetail) return null;
    return `${compareAlt.trickIndex}:${compareDetail.humanSeat}`;
  }, [compareAlt, compareDetail]);
  const hardDiffKeys = useMemo(() => {
    if (!diffSeatKeys) return null;
    const hard = new Set<string>();
    for (const k of diffSeatKeys) {
      if (equivVariantKeys?.has(k)) continue;
      if (bifurcationOriginKey != null && k === bifurcationOriginKey) continue;
      hard.add(k);
    }
    return hard.size > 0 ? hard : null;
  }, [diffSeatKeys, equivVariantKeys, bifurcationOriginKey]);

  if (!open) return null;

  const sideBySide = compareDetail != null && !narrowViewport;
  const showStudiedPane = !compareDetail || sideBySide || activePane === 'studied';
  const showComparePane = compareDetail != null && (sideBySide || activePane === 'compare');
  const sharedPrefixPct = studiedDetail?.prefixMatchPctByTrick;
  const sharedPrefixSample = studiedDetail?.prefixMatchSampleSize;

  const differeId = studiedDetail?.differeId;
  const debutLabel = differeMeta?.debut
    ? new Date(differeMeta.debut).toLocaleDateString('fr-FR', {
        day: 'numeric', month: 'short', year: 'numeric',
      })
    : null;

  const legendFor = (
    detail: DiffereStatsDealDetail,
    opts: {
      showAlternatives: boolean;
      hypCards?: number[] | null;
      diffKeys?: Set<string> | null;
      equivVariantKeys?: Set<string> | null;
      bifurcationOriginKey?: string | null;
    },
  ) => (
    <p className="admin-muted differe-stats-deal-detail-meta">
      {detail.tricks.length} plis — preneur{' '}
      {['Sud', 'Est', 'Nord', 'Ouest'][detail.takerPosition] ?? '?'}
      {opts.hypCards != null && opts.hypCards.length > 0 && (
        <> — surlignage hypothèse pli 1→{opts.hypCards.length}</>
      )}
      {opts.diffKeys != null && opts.diffKeys.size > 0 && (
        <> — violet : cartes différentes</>
      )}
      {opts.equivVariantKeys != null && opts.equivVariantKeys.size > 0 && (
        <> — teal : équiv. (classe ou issue)</>
      )}
      {opts.bifurcationOriginKey != null && (
        <> — rouge : carte génératrice de la bifurcation</>
      )}
      {sharedPrefixSample != null && sharedPrefixSample > 0 && (
        <> — % top-{sharedPrefixSample} ; (ou X) = équiv. ; vert = bif. ; ambre = reportée ; bleu = variante avant bif. (² = type 2)</>
      )}
      <> — estompé = seule carte/classe jouable</>
      {' · '}
      clic case = preview au survol
    </p>
  );

  const comparePseudos = compareAlt
    ? (compareAlt.bestPseudos?.length ? compareAlt.bestPseudos : [compareAlt.bestPseudo])
    : null;

  const renderPane = (
    detail: DiffereStatsDealDetail,
    opts: {
      role: 'studied' | 'compare';
      titlePrefix: string;
      showAlternatives: boolean;
      diffKeys?: Set<string> | null;
      equivVariantKeys?: Set<string> | null;
      bifurcationOriginKey?: string | null;
      hypCards?: number[] | null;
      peerCardsByTrick?: number[][];
      playerLabel?: ReactNode;
      forcedPlaySeatKeys?: ReadonlySet<string>;
    },
  ) => (
    <div className={`differe-stats-deal-detail-pane differe-stats-deal-detail-pane--${opts.role}`}>
      <div className="differe-stats-deal-detail-pane-head">
        <h5>
          {opts.titlePrefix}
          {opts.playerLabel ?? (detail.pseudoDisplay || detail.pseudo)}
          {detail.dealScore != null ? ` — ${detail.dealScore}` : ''}
          {detail.perDealRank != null
            ? ` (rang ${formatPerDealRankLabel(detail.perDealRank, detail.perDealRankExAequo)})`
            : ''}
        </h5>
        <div className="differe-stats-deal-detail-pane-actions">
          <button type="button" className="admin-btn admin-btn-sm" onClick={() => openInTraining(detail)}>
            Entraînement
          </button>
          {compareDetail && (
            <button
              type="button"
              className="differe-stats-deal-detail-pane-close"
              aria-label={
                opts.role === 'compare'
                  ? 'Fermer la bifurcation'
                  : 'Remplacer le joueur étudié par la bifurcation'
              }
              onClick={opts.role === 'compare' ? clearCompare : promoteCompareToStudied}
            >
              ×
            </button>
          )}
        </div>
      </div>
      <DiffereStatsDealTricksGrid
        tricks={detail.tricks}
        humanSeat={detail.humanSeat}
        hypothesisHumanCards={opts.hypCards ?? undefined}
        prefixMatchPctByTrick={sharedPrefixPct}
        betterScoreAlternativesByTrick={
          opts.showAlternatives ? detail.betterScoreAlternativesByTrick : undefined
        }
        peerCardsByTrick={opts.peerCardsByTrick}
        focusedTrickNum1={trickFocus}
        onTrickNumberCellClick={toggleTrickFocus}
        onAlternativeClick={
          opts.showAlternatives
            ? (alt, trickIndex) => void openAlternative(alt, trickIndex)
            : undefined
        }
        diffSeatKeys={opts.diffKeys ?? undefined}
        equivVariantSeatKeys={opts.equivVariantKeys ?? undefined}
        bifurcationOriginSeatKey={opts.bifurcationOriginKey ?? undefined}
        highlightLead={false}
        hoverPreviewActive={hoverPreviewActive}
        onToggleHoverPreview={toggleHoverPreview}
        onHoverPreviewTarget={(target, x, y) => handleHoverPreviewTarget(detail, target, x, y)}
        forcedPlaySeatKeys={opts.forcedPlaySeatKeys}
      />
      {legendFor(detail, opts)}
      <DiffereStatsDealHandsPanel
        detail={detail}
        trickFocusTrickNum1={trickFocus}
        studiedSeat={detail.humanSeat}
      />
    </div>
  );

  return (
    <div
      className="differe-stats-drill-overlay"
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <div
        className={`differe-stats-drill-modal admin-card${compareDetail ? ' differe-stats-drill-modal--compare' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="differe-stats-toolbar">
          <div className="differe-stats-deal-detail-heading">
            {differeId != null && (
              <p className="differe-stats-deal-detail-differe-line">
                Différé #{differeId}
                {differeMeta?.type ? ` · ${differeMeta.type}` : ''}
                {debutLabel ? ` · début ${debutLabel}` : ''}
              </p>
            )}
            <h4>
              {studiedDetail
                ? `Donne #${studiedDetail.dealIndex + 1} (${studiedDetail.contractLabel})`
                : 'Détail partie'}
            </h4>
          </div>
          <div className="differe-stats-params">
            <DiffereStatsEquivModeToggle
              value={modalEquivMode}
              onChange={mode => void setModalEquivAndRefetch(mode)}
              disabled={!studiedDetail || loading}
              showLowRankHint
              lowRankLimit={lowRankLimit}
            />
            <label
              className="differe-stats-check"
              title="Type 1 : reportée → variante si 1ère carte ≠ au pli d'effet = humain. Type 2 : mention plus précoce avec même effet + même gain qu'une bif. plus tardive (affichée en bleu² pour test)."
            >
              <input
                type="checkbox"
                checked={smartBif}
                disabled={!studiedDetail || loading}
                onChange={e => void setSmartBifAndRefetch(e.target.checked)}
              />
              smart
            </label>
            <button type="button" className="admin-btn" onClick={onClose}>
              Fermer
            </button>
          </div>
        </div>

        {error && <p className="admin-error-text">{error}</p>}

        {studiedDetail && compareDetail && narrowViewport && (
          <div className="differe-stats-deal-compare-tabs">
            <div className={`differe-stats-deal-compare-tab${activePane === 'studied' ? ' active' : ''}`}>
              <button
                type="button"
                className="differe-stats-deal-compare-tab-main"
                onClick={() => setActivePane('studied')}
              >
                Étudié · {studiedDetail.pseudoDisplay || studiedDetail.pseudo}
              </button>
              <button
                type="button"
                className="differe-stats-deal-compare-tab-close"
                aria-label="Remplacer le joueur étudié par la bifurcation"
                onClick={promoteCompareToStudied}
              >
                ×
              </button>
            </div>
            <div className={`differe-stats-deal-compare-tab${activePane === 'compare' ? ' active' : ''}`}>
              <button
                type="button"
                className="differe-stats-deal-compare-tab-main"
                onClick={() => setActivePane('compare')}
              >
                Bifurcation ·{' '}
                {comparePseudos ? (
                  <DiffereStatsPseudoPlusBubble
                    pseudos={comparePseudos}
                    primary={compareDetail.pseudo}
                    primaryLabel={compareDetail.pseudoDisplay || compareDetail.pseudo}
                  />
                ) : (
                  (compareDetail.pseudoDisplay || compareDetail.pseudo)
                )}
              </button>
              <button
                type="button"
                className="differe-stats-deal-compare-tab-close"
                aria-label="Fermer la bifurcation"
                onClick={clearCompare}
              >
                ×
              </button>
            </div>
          </div>
        )}

        {studiedDetail && (
          <div className={`differe-stats-deal-detail-body${sideBySide ? ' differe-stats-deal-detail-body--split' : ''}`}>
            {showStudiedPane && renderPane(studiedDetail, {
              role: 'studied',
              titlePrefix: compareDetail ? 'Étudié · ' : '',
              showAlternatives: true,
              hypCards: hypothesisCards,
              diffKeys: compareDetail ? null : hardDiffKeys,
              forcedPlaySeatKeys: forcedKeysStudied,
            })}
            {showComparePane && compareDetail && renderPane(compareDetail, {
              role: 'compare',
              titlePrefix: 'Bifurcation · ',
              showAlternatives: false,
              hypCards: hypothesisCards,
              diffKeys: hardDiffKeys,
              equivVariantKeys,
              bifurcationOriginKey,
              peerCardsByTrick: compareAlt?.peerCardsByTrick,
              forcedPlaySeatKeys: forcedKeysCompare,
              playerLabel: comparePseudos ? (
                <DiffereStatsPseudoPlusBubble
                  pseudos={comparePseudos}
                  primary={compareDetail.pseudo}
                  primaryLabel={compareDetail.pseudoDisplay || compareDetail.pseudo}
                />
              ) : undefined,
            })}
          </div>
        )}

        {loading && (
          <div className="differe-stats-deal-detail-loading" role="status" aria-live="polite">
            <span className="differe-stats-deal-detail-spinner" aria-hidden />
            <span>Chargement de la partie…</span>
          </div>
        )}
      </div>

      {hoverPreviewActive && hoverPreview && createPortal(
        <DiffereStatsTrickHoverPreview
          detail={hoverPreview.detail}
          target={hoverPreview.target}
          x={hoverPreview.x}
          y={hoverPreview.y}
        />,
        document.body,
      )}
    </div>
  );
}
